import { faSync } from '@fortawesome/pro-light-svg-icons'
import { useCallback, useState } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { UserPermissionsSingle } from '../../../../../../../../baseComponents/CreateRoutering/types'
import DisplayPrice from '../../../../../../../../baseComponents/Displays/DisplayPrice'
import { CalculateParamsTy } from '../../../../../../../../baseComponents/Displays/DisplayPrice/types'
import { ModalOpenedSetTy } from '../../../../../../../../baseComponents/Modal/types'
import { ModalCreateEditBase } from '../../../../../../../../baseComponents/ModalsLayouts/ModalCreateEdit'
import { permissionCheck } from '../../../../../../../../baseComponents/PermissionChecker/function'
import TwinIcon from '../../../../../../../../baseComponents/TwinIcon'
import TableWithoutVirtualListing, { TableWithoutDefaultRowRenderer } from '../../../../../../../../baseComponents/TwinTable/TableWithoutVirtualListing'
import { TableWithoutVirtualListingRowProps } from '../../../../../../../../baseComponents/TwinTable/TableWithoutVirtualListing/types'
import { RowData } from '../../../../../../../../baseComponents/TwinTable/types'
import { CustomRenderBoolean, CustomRenderString } from '../../../../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ColumnTableSchema } from '../../../../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import { RectangularInput, RectangularInputFormatNumberSmall } from '../../../../../../../../forms/Input'
import { BeforeSubmitHandler, OnSubmit } from '../../../../../../../../forms/TwinForm/types'
import { TwinDictionary } from '../../../../../../../../utils/globals/dictionary'
import { getSeparatorsOfFormatedNumber, replaceDotsForDecimalSeparator } from '../../../../../../../../utils/globals/numbers'
import { twinPreparePrice } from '../../../../../../../../utils/globals/price'
import withLoading from '../../../../../../../../utils/hoc/withLoading'
import useTwinTranslation from '../../../../../../../../utils/hooks/useTwinTranslation'
import { AllReduxPayloads } from '../../../../../../../../utils/reducers'
import { getPreparePricesConfigParam } from '../../../../../../../../utils/reducers/getters'
import { SpecificPriceRulesPayload } from '../../../../../../../../utils/reducers/products/specificPriceRules'
import { displayUnitsIcon } from '../../../../../../../Price/DisplayPrice'
import { parseVariantsToSpecificPriceRules } from '../../functions'
import { EditVariantPricesData, EditVariantPricesTy, ProductVariantEditSpecificPriceProduct } from '../../types'
import { getLang } from '../../../../../../../../utils/globals'
import { ProductExtendedTyShort } from '../../../../../../types'

interface EditVariantsPricesProps extends EditVariantsPricesLogicProps {
    allRowData?: RowData
    userPermissions: UserPermissionsSingle
}

const EditVariantsPrices: React.FC<EditVariantsPricesProps & ReduxSpecificPriceRules> = ({ onSubmit, items, specificPriceRules, userPermissions, setOpened, allRowData }) => {
    const { data, beforeSubmit, handleOnSubmit, setData } = useEditVariantsPricesLogic({ items, specificPriceRules, onSubmit, setOpened })
    const { t } = useTwinTranslation()
    const renderThis: JSX.Element[] = []
    for (const key in data) {
        const element = data[key]
        renderThis.push(<SingleTable ProductId={element.dataProduct.id} singleProductData={element} userPermissions={userPermissions} setData={setData} />)
    }
    return (
        <ModalCreateEditBase url={'/api/app/product/specificPrice/updateProductSpecificPriceBulk'} onSubmit={handleOnSubmit} translations={{ title: t('updateVariants', 'Editar variantes'), button: t('save', 'Guardar') }} haveButtonPermissions={true} fields={[]} setOpened={() => { }} beforeSubmit={beforeSubmit} className='specificPricesBulkEdit'>
            {renderThis}
        </ModalCreateEditBase>
    )
}

interface EditVariantsPricesLogicProps {
    items: ProductExtendedTyShort[]
    specificPriceRules: SpecificPriceRulesPayload
    onSubmit: OnSubmit
    setOpened: ModalOpenedSetTy
}

const useEditVariantsPricesLogic = ({ items, specificPriceRules, onSubmit, setOpened }: EditVariantsPricesLogicProps) => {
    const [data, setData] = useState<EditVariantPricesTy>(() => {
        if (specificPriceRules?.dictionary) {
            return parseVariantsToSpecificPriceRules(specificPriceRules?.dictionary, items) 
        }
        return {}
    })

    const beforeSubmit: BeforeSubmitHandler = useCallback((vals) => {
        const result: TwinDictionary[] = []
        for (const ProductId in data) {
            const element = {
                ProductId,
                specificPriceRules: data[ProductId].prices
            }
            result.push(element)
        }
        vals.productSpecificPriceBulk = result
        return true
    }, [data])

    const handleOnSubmit: OnSubmit = useCallback((rest, vals) => {
        onSubmit(rest, vals)
        setOpened(null) 
    }, [onSubmit, setOpened]) 

    return { beforeSubmit, data, handleOnSubmit, setData }
}

interface SingleTableProps extends SingleTableLogicProps {
    singleProductData: EditVariantPricesData
    userPermissions: UserPermissionsSingle
}

const SingleTable: React.FC<SingleTableProps> = ({singleProductData, userPermissions, ...logic}) => {
    const { changeIsPack, changePrice, syncWithParent, addUnits, deleteUnits, changeUnits } = useSingleTableLogic({ ...logic })
    const { t } = useTwinTranslation()
    const calculateParamsDefault = getPreparePricesConfigParam() as CalculateParamsTy
    const lang = getLang()
    let productName = ''
    for (const element of singleProductData?.dataProduct?.ProductLangs) {
        if (element.LangId === lang) {
            productName = element.name
        }
    }

    const columns: ColumnTableSchema<ProductVariantEditSpecificPriceProduct> = [
        {
            id: 'id',
            dataKey: 'id',
            label: t('id', 'ID'),
            customRender: (parameterValue) => <CustomRenderString value={'T' + parameterValue} />,
            width: 50
        },
        {
            id: 'name',
            dataKey: 'name',
            label: t('name', 'Nombre'), 
            customRender: (value) => <span className='twin_elipsis'>{value}</span>
        },
        {
            id: 'units',
            dataKey: 'units',
            label: t('fromU', 'A partir de...'),
            toolTip: t('explanationUnitsPrice', 'Es la cantidad a partir de la cual se aplica un precio. En caso de marcar que es un pack, es el conjunto de unidades que componen el pack.'),
            customRender: (parameterValue, allRowData) => <RectangularInputFormatNumberSmall value={parameterValue ?? 1} readOnly={!permissionCheck(userPermissions, 'update')} onChange={(value) => changeUnits(value, allRowData.rowIndex)} className='w-80' />,
            width: 120,
        },
        {
            id: 'is_pack',
            dataKey: 'is_pack',
            label: t('pack', 'Pack'),
            toolTip: t('explanationPack', 'El pack es un conjunto de unidades que se venden de forma conjunta. Si está marcado, el precio se aplicará sobre el conjunto de unidades.'),
            width: 80,
            customRender: (parameterValue, allRowData) => <CustomRenderBoolean onClick={() => permissionCheck(userPermissions, 'update') && changeIsPack(allRowData.rowIndex)} value={String(parameterValue) === 'true'} width={50} />,
        },
        {
            id: 'price',
            dataKey: 'price',
            label: t('price', 'Precio'),
            toolTip: t('explanationPriceOrFormula', 'Es el precio o la fórmula por unidad. En el caso de que se trate de un pack, el precio es sobre el conjunto de unidades.'),
            customRender: (parameterValue, allRowData) => <RectangularInput value={replaceDotsForDecimalSeparator(parameterValue ?? allRowData.formula ?? '')} readOnly={!permissionCheck(userPermissions, 'update')} onChange={(value) => changePrice(value, allRowData.rowIndex)}></RectangularInput>
        },
        {
            id: 'calculated_price',
            dataKey: 'calculated_price',
            label: t('finalPrice', 'Precio final'),
            customRender: (_parameterValue, allRowData) => {
                return <DisplayPrice value={allRowData?.calculated_price} CurrencyId={allRowData?.CurrencyId || ''} with_vat={allRowData?.with_vat} calculateParams={{
                    decimals: allRowData?.decimals,
                    rounding: allRowData?.rounding,
                    roundingPriceType: calculateParamsDefault?.roundingPriceType
                }} />
            },
            minWidth: 100
        },
        {
            id: '_old_price',
            dataKey: '_old_price',
            label: t('lastPricePrevious', 'Precio anterior'),
            customRender: (_parameterValue, allRowData) => {
                return <DisplayPrice value={parseInt(_parameterValue)} CurrencyId={allRowData?.CurrencyId || ''} with_vat={allRowData?.with_vat} calculateParams={{
                    decimals: allRowData?.decimals,
                    rounding: allRowData?.rounding,
                    roundingPriceType: calculateParamsDefault?.roundingPriceType
                }} />
            },
            minWidth: 100
        },
        {
            id: 'sync',
            dataKey: 'id',
            label: '',
            simpleLabel: t('restore', 'Restablecer'),
            width: 50,
            customRender: (_parameterValue, allRowData) => {
                return < TwinIcon icon={faSync} onClick={() => syncWithParent(allRowData)} className={allRowData.price === null || allRowData.price === '' ? 'text-green-21' : 'text-red-BA'} />
            },
            havePermission: permissionCheck(userPermissions, 'update'),
        },
        {
            id: 'delete',
            dataKey: 'id',
            label: '',
            simpleLabel: t('deleteLine', 'Eliminar línea'),
            width: 50,
            customRender: (_parameterValue, allRowData) => displayUnitsIcon(allRowData, addUnits, deleteUnits),
            havePermission: permissionCheck(userPermissions, 'update'),
        },
    ]
    return (
        <div>
            <span className='regular16 mb-10 block'>{productName} - {singleProductData?.dataProduct?.reference}</span>
            <TableWithoutVirtualListing className='table_small_padding' data={singleProductData.prices} columns={columns} disableScroll={true} rowRenderer={displayEditVariantPricesRowData}/>
        </div>
    )

}

interface SingleTableLogicProps {
    ProductId: number
    setData: React.Dispatch<React.SetStateAction<EditVariantPricesTy>>
}

const useSingleTableLogic = ({ setData, ProductId }: SingleTableLogicProps) => {
    const changePrice = useCallback((price: string | null, rowIndex: number) => {
        setData((oldData) => {
            if (!oldData) {
                return oldData
            }
            const { decimalSeparator } = getSeparatorsOfFormatedNumber()
            let newValue = ''
            if (price) {
                newValue = price.replace(decimalSeparator, '.')
            }
            const newDataCopy = JSON.parse(JSON.stringify(oldData))
            newDataCopy[ProductId].prices[rowIndex] = {
                ...newDataCopy[ProductId].prices[rowIndex],
                'price': price === null ? null : newValue
            }
            const prices = twinPreparePrice([...newDataCopy[ProductId].prices])
            newDataCopy[ProductId].prices = prices
            return newDataCopy
        })
    }, [setData, ProductId])

    const syncWithParent = useCallback(async (allRowData: RowData) => {
        changePrice(null, allRowData.rowIndex)
    }, [changePrice])

    const changeIsPack = useCallback((rowIndex: number) => {
        setData((oldData) => {
            if (!oldData) {
                return oldData
            }
            const newDataCopy = JSON.parse(JSON.stringify(oldData))
            if (newDataCopy[ProductId].prices && newDataCopy[ProductId].prices.length) {
                newDataCopy[ProductId].prices[rowIndex] = {
                    ...newDataCopy[ProductId].prices[rowIndex],
                    'is_pack': !newDataCopy[ProductId].prices[rowIndex].is_pack
                } 
            }
            return newDataCopy
        })
    }, [setData, ProductId])

    const changeUnits = useCallback((units: string, rowIndex: number) => {
        setData((oldData) => {
            if (!oldData) {
                return oldData
            }
            const newDataCopy = JSON.parse(JSON.stringify(oldData))
            if (!newDataCopy[ProductId].prices[rowIndex].old_units) {
                newDataCopy[ProductId].prices[rowIndex].old_units = newDataCopy[ProductId].prices[rowIndex].units || 1
            }
            newDataCopy[ProductId].prices[rowIndex] = { ...newDataCopy[ProductId].prices[rowIndex], 'units': units ? parseInt(units) : '' }
            const prices = twinPreparePrice([...newDataCopy[ProductId].prices])
            newDataCopy[ProductId].prices = prices
            return newDataCopy
        })
    }, [setData, ProductId])

    const deleteUnits = useCallback((rowIndex: number) => {
        setData((oldData) => {
            if (!oldData) {
                return oldData
            }
            const newDataCopy = JSON.parse(JSON.stringify(oldData))
            if (ProductId) {
                const myData = newDataCopy[ProductId].prices[rowIndex]
                newDataCopy[ProductId].prices[rowIndex] = { ...myData, deleted: !myData.deleted }
                const prices = twinPreparePrice([...newDataCopy[ProductId].prices])
                newDataCopy[ProductId].prices = prices
            }
            return newDataCopy
        })
    }, [setData, ProductId])

    const addUnits = useCallback((rowIndex: number) => {
        setData((oldData) => {
            if (!oldData) {
                return oldData
            }
            const newDataCopy = JSON.parse(JSON.stringify(oldData))
            if (ProductId) {
                const myData = newDataCopy[ProductId].prices[rowIndex]
                let last = rowIndex
                for (const key in newDataCopy[ProductId].prices) {
                    const element = newDataCopy[ProductId].prices[key]
                    const units = element.units || 1
                    if (element.tag.includes(myData.tag)) {
                        if (newDataCopy[ProductId].prices[last].units < units) {
                            last = parseInt(key)
                        }
                    }
                }
                newDataCopy[ProductId].prices.splice(last + 1, 0, { ...myData, units: (newDataCopy[ProductId].prices[last].units || 1) + 1, old_units: -1 })
                const prices = twinPreparePrice([...newDataCopy[ProductId].prices])
                newDataCopy[ProductId].prices = prices
            }
            return newDataCopy
        })
    }, [setData, ProductId])

    return {addUnits, changeIsPack, changePrice, changeUnits, deleteUnits, syncWithParent}
}

const displayEditVariantPricesRowData = (props: TableWithoutVirtualListingRowProps) => {
    let className = props.className
    if (props?.rowData?.tag?.includes('[')) {
        className += ' bg-gray-F7'
    }
    if (props.rowData.deleted) {
        className += ' bg-red-FF'
    }
    return <TableWithoutDefaultRowRenderer {...props} className={className} />
}


const specificPriceRulesDispatch = {
    setSpecificPriceRules: (payload: SpecificPriceRulesPayload) => ({ type: 'CHANGE_SPECIFICPRICERULES', payload }),
}

type ReduxSpecificPriceRules = ConnectedProps<typeof specificPriceRulesConnect>
const mapSpecificPriceRulesConnector = (state: AllReduxPayloads) => ({ specificPriceRules: state.specificPriceRules })
const specificPriceRulesConnect = connect(mapSpecificPriceRulesConnector, specificPriceRulesDispatch)

const specificPriceRulesConnectLoading = withLoading(EditVariantsPrices, [ { fetchUrl: '/api/app/product/specificPrice/getAllSpecificPriceRules', propName: 'specificPriceRules', setFunctionName: 'setSpecificPriceRules' }])
const EditVariantsPricesConnect = specificPriceRulesConnect(specificPriceRulesConnectLoading)

export default EditVariantsPricesConnect